import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import { DataDefinitionList, Loader, Notification, Message } from '../../web.common';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import { DisplayGuid, DisplayBoolean, DisplayDate, DisplayType } from '../items';
import { DetailsRightBar, Divider, NoLink, Action } from './DetailsRightBar';

const unitsHeader = [
  {
    name: 'Id',
    label: 'Id',
    render: ({ id }) => <DisplayGuid short>{id}</DisplayGuid>,
  },
  {
    name: 'ExternalId',
    label: 'External Id',
    render: ({ externalId }) => <span>{externalId}</span>,
  },
  {
    name: 'Name',
    label: 'Name',
    render: ({ name }) => <span>{name}</span>,
  },
  {
    name: 'Type',
    label: 'Type',
    render: ({ type }) => type ? <DisplayType>{type}</DisplayType> : <span>-</span>,
  },
  {
    name: 'Placement',
    label: 'Placement',
    render: ({ placement }) => <span>{placement || '-'}</span>,
  },
  {
    name: 'Description',
    label: 'Description',
    render: ({ description }) => <span>{description || '-'}</span>,
  },
];

const NonPartnerFacilityInternal = (props) => {
  const [facility, setFacility] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isActioning, setIsActioning] = useState(false);

  const { match: { params: { id } } } = props;

  const loadData = async () => {
    try {
      const data = await props.api.nonPartnerFacilities.getNonPartnerFacility(id);
      setFacility(data);
    } catch (e) {
      setNotification(new Message('error', 'Unable to load data'));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAddToBlacklist = async () => {
    if (!confirm(`Add "${facility.name}" to blacklist?`)) return;
    setIsActioning(true);
    try {
      await props.api.nonPartnerFacilities.addToBlacklist(facility.id);
      setNotification(new Message('success', 'Added to blacklist successfully'));
      await loadData();
    } catch (e) {
      setNotification(new Message('error', `Unable to add to blacklist${e?.payload?.translatedMessage ? `: ${e.payload.translatedMessage}` : ''}`));
    } finally {
      setIsActioning(false);
    }
  };

  const handleRemoveFromBlacklist = async () => {
    if (!confirm(`Remove "${facility.name}" from blacklist?`)) return;
    setIsActioning(true);
    try {
      await props.api.nonPartnerFacilities.removeFromBlacklist(facility.blacklistId);
      setNotification(new Message('success', 'Removed from blacklist successfully'));
      await loadData();
    } catch (e) {
      setNotification(new Message('error', `Unable to remove from blacklist${e?.payload?.translatedMessage ? `: ${e.payload.translatedMessage}` : ''}`));
    } finally {
      setIsActioning(false);
    }
  };

  const handleSoftDelete = async () => {
    if (!confirm(`Delete "${facility.name}"?`)) return;
    setIsActioning(true);
    try {
      await props.api.nonPartnerFacilities.softDelete(facility.id);
      setNotification(new Message('success', 'Facility deleted successfully'));
      await loadData();
    } catch (e) {
      setNotification(new Message('error', `Unable to delete facility${e?.payload?.translatedMessage ? `: ${e.payload.translatedMessage}` : ''}`));
    } finally {
      setIsActioning(false);
    }
  };

  const handleRestore = async () => {
    if (!confirm(`Restore "${facility.name}"?`)) return;
    setIsActioning(true);
    try {
      await props.api.nonPartnerFacilities.restore(facility.id);
      setNotification(new Message('success', 'Facility restored successfully'));
      await loadData();
    } catch (e) {
      setNotification(new Message('error', `Unable to restore facility${e?.payload?.translatedMessage ? `: ${e.payload.translatedMessage}` : ''}`));
    } finally {
      setIsActioning(false);
    }
  };

  let mappedData = null;

  if (facility) {
    mappedData = {
      Id: <DisplayGuid>{facility.id}</DisplayGuid>,
      Name: <span>{facility.name}</span>,
      Slug: <span>{facility.slug}</span>,
      Area: <span>{facility.area || '-'}</span>,
      'Area Slug': <span>{facility.areaSlug || '-'}</span>,
      Blacklisted: <DisplayBoolean>{facility.isBlacklisted}</DisplayBoolean>,
      ...(facility.isBlacklisted && facility.blacklistReason ? { 'Blacklist Reason': <span>{facility.blacklistReason}</span> } : {}),
      'Is Deleted': <DisplayBoolean>{facility.isDeleted}</DisplayBoolean>,
      'Source Type': facility.sourceType ? <DisplayType>{facility.sourceType}</DisplayType> : <span>-</span>,
      'External Id': <span>{facility.externalId || '-'}</span>,
      'Country Code': <span>{facility.countryCode || '-'}</span>,
      Kommune: <span>{facility.kommune || '-'}</span>,
      Region: <span>{facility.region || '-'}</span>,
      Address: <span>{facility.address || '-'}</span>,
      Website: facility.website
        ? <a href={facility.website} target='_blank' rel='noopener noreferrer'>{facility.website}</a>
        : <span>-</span>,
      Phone: <span>{facility.contactPhone || '-'}</span>,
      Email: <span>{facility.contactEmail || '-'}</span>,
      Language: <span>{facility.language || '-'}</span>,
      'Owner Type': <span>{facility.ownerType || '-'}</span>,
      'Operator Type': <span>{facility.operatorType || '-'}</span>,
      Latitude: <span>{facility.latitude ?? '-'}</span>,
      Longitude: <span>{facility.longitude ?? '-'}</span>,
      'Created At': <DisplayDate>{facility.createdAtUtc}</DisplayDate>,
      'Updated At': <DisplayDate>{facility.updatedAtUtc}</DisplayDate>,
    };
  }

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <div className='content-with-right-pane-delimiter'>
            <PageTop
              documentTitle='Non Partner Facility'
              documentDescription='Non Partner Facility details'
            />
            <h2 className='main-page-header main-page-header-with-subheader'>
              Non Partner Facility
            </h2>
            <h5 className='main-page-subheader'>{id}</h5>
            <br />
            {mappedData ? (
              <React.Fragment>
                <DataDefinitionList
                  listData={mappedData}
                  classNames={['detail-page-list']}
                />
                {facility.units && facility.units.length > 0 && (
                  <article className='data-table-wrap'>
                    <h3 className='main-page-header main-page-header-with-subheader'>
                      Units ({facility.units.length})
                    </h3>
                    <br />
                    <table className='data-lists component-data-table cell-content-align-top'>
                      <thead>
                        <tr>
                          {unitsHeader.map((col) => (
                            <td key={col.name}>{col.label}</td>
                          ))}
                        </tr>
                      </thead>
                      {facility.units.map((unit) => (
                        <tbody key={unit.id}>
                          <tr>
                            {unitsHeader.map((col) => (
                              <td key={col.name}>
                                <strong className='table-cell-label'>{col.label}</strong>
                                {col.render(unit)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      ))}
                    </table>
                  </article>
                )}
              </React.Fragment>
            ) : (
              <Loader hasLoaded={false}>Loading...</Loader>
            )}
          </div>
          <DetailsRightBar>
            <Divider>Links</Divider>
            <NoLink>None</NoLink>
            <Divider>Actions</Divider>
            {facility && (
              <>
                {facility.isBlacklisted ? (
                  <Action action={handleRemoveFromBlacklist} disabled={isActioning}>
                    Remove from Blacklist
                  </Action>
                ) : facility.isDeleted ? (
                  <Action action={handleRestore} disabled={isActioning}>
                    Restore
                  </Action>
                ) : (
                  <>
                    <Action action={handleAddToBlacklist} disabled={isActioning}>
                      Add to Blacklist
                    </Action>
                    <Action action={handleSoftDelete} disabled={isActioning}>
                      Delete
                    </Action>
                  </>
                )}
              </>
            )}
          </DetailsRightBar>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const NonPartnerFacility = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(NonPartnerFacilityInternal)
  )
);

NonPartnerFacility.requiresLogin = true;
NonPartnerFacility.propTypes = { isLoggedIn: PropTypes.bool };

export default NonPartnerFacility;
